import { PayloadAction, PayloadActionCreator } from "@reduxjs/toolkit";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { merge, Subject } from "rxjs";
import {
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  take,
} from "rxjs/operators";
import { AppStore, store } from "../..";
import { store$ } from "../../utils";
import { Fetchable, isFetchableDone, isFetchableSuccess } from "./helpers";

export interface RemoteResource<
  Resource,
  FetchReturn = Resource,
  FetchArgs = void
> {
  fetch: PayloadActionCreator<FetchArgs>;
  updateStore: (payload: any) => PayloadAction<Fetchable<FetchReturn>, any>;
  selector: (s: AppStore) => Fetchable<Resource>;
}

export function useFetchable<T, FetchReturn = T, FetchArgs = void>(
  resource: RemoteResource<T, FetchReturn, FetchArgs>,
  fetchArgs?: FetchArgs
): Fetchable<T> {
  const dispatch = useDispatch();
  const fetched = useSelector<AppStore, Fetchable<T>>(resource.selector);

  const fetched$ = useMemo(() => new Subject<Fetchable<T>>(), []);
  const fetchArgs$ = useMemo(() => new Subject<FetchArgs>(), []);

  useEffect(() => {
    // Allow re-fetching of errored fetchable only once
    const errored$ = fetched$.pipe(
      filter(({ status }) => status === "error"),
      take(1)
    );

    // Fetch fetchable when empty
    const empty$ = fetched$.pipe(
      distinctUntilChanged(),
      filter(({ status }) => status === "empty")
    );

    const sub = merge(errored$, empty$).subscribe((args) =>
      dispatch(resource.fetch(fetchArgs))
    );

    return () => sub.unsubscribe();
  }, [resource, dispatch]);

  useEffect(() => {
    fetched$.next(fetched);
  }, [fetched]);

  useEffect(() => {
    fetchArgs$.next(fetchArgs);
  }, [fetchArgs]);

  return fetched;
}

export async function ensureResource<
  Resource,
  FetchReturn = Resource,
  FetchArgs = void
>(r: RemoteResource<Resource, FetchReturn, FetchArgs>): Promise<Resource> {
  const resource$ = store$.pipe(map(r.selector));

  const resultPromise = new Promise<Resource>((res, rej) => {
    resource$
      .pipe(distinctUntilKeyChanged("status"), filter(isFetchableDone), take(1))
      .subscribe((result) => {
        if (isFetchableSuccess(result)) {
          res(result.data);
        } else {
          rej(result.err);
        }
      }, rej);
  });

  resource$
    .pipe(
      filter((x) => x.status === "empty"),
      take(1)
    )
    .subscribe(() => store.dispatch(r.fetch(undefined)));

  return resultPromise;
}
